/**
 * Sensitivity-label coverage analysis (C12). Pure aggregation over per-site scans,
 * plus a label filter so the same scan answers "what % carry ANY label" or "what %
 * carry <specific label>" without re-scanning.
 */
import type { SiteLabelCoverage } from '@/graph/modules/labels';

export interface LabelCoverageResult {
  sitesScanned: number;
  totalFiles: number;
  totalLabeled: number;
  overallPct: number; // 0..100
  perSite: SiteLabelCoverage[];
  labels: string[]; // distinct label names seen, for the filter dropdown
}

/** Percentage of a site's scanned files carrying a label (any, or a specific one). */
export function siteCoveragePct(site: SiteLabelCoverage, labelFilter: string | null): number {
  if (site.scanned === 0) return 0;
  const labeled = labelFilter === null ? site.labeled : site.byLabel[labelFilter] ?? 0;
  return Math.round((labeled / site.scanned) * 100);
}

export function aggregateLabelCoverage(perSite: SiteLabelCoverage[]): LabelCoverageResult {
  const totalFiles = perSite.reduce((n, s) => n + s.scanned, 0);
  const totalLabeled = perSite.reduce((n, s) => n + s.labeled, 0);
  const labels = new Set<string>();
  for (const s of perSite) for (const name of Object.keys(s.byLabel)) labels.add(name);

  // Sites with the least coverage first — that's where the risk is.
  const sortedSites = [...perSite].sort(
    (a, b) => siteCoveragePct(a, null) - siteCoveragePct(b, null),
  );

  return {
    sitesScanned: perSite.length,
    totalFiles,
    totalLabeled,
    overallPct: totalFiles > 0 ? Math.round((totalLabeled / totalFiles) * 100) : 0,
    perSite: sortedSites,
    labels: [...labels].sort(),
  };
}

/** Overall % for a chosen label filter across all scanned files. */
export function overallCoverageForLabel(result: LabelCoverageResult, labelFilter: string | null): number {
  if (result.totalFiles === 0) return 0;
  const labeled =
    labelFilter === null
      ? result.totalLabeled
      : result.perSite.reduce((n, s) => n + (s.byLabel[labelFilter] ?? 0), 0);
  return Math.round((labeled / result.totalFiles) * 100);
}
